// src/lib/plBasis.ts (2026-09-11 — 経営PL タブの派生指標)
//
// 病院会計準則の PL は
//   医業利益 = 医業収益計 − 材料費計 − 人件費計 − 委託費計 − 設備関係費計
//              − (経費計 + 本部経費 + 研究費 + 研修費)
//   経常利益 = 医業利益 + 医業外損益            (医業外損益 = 雑収入 + 受取利息 − 雑損失 − 支払利息)
// という構造。 ダッシュボードはこれをそのまま見せる:
//
//   経費等     = 経費計 + 本部経費 + 研究費 + 研修費   … **常にこの式**(利益の段階で変わらない)
//   医業外損益 = 雑収入 + 受取利息 − 雑損失 − 支払利息 … **独立した 1 項目**として持つ
//
// したがって
//   医業収益計 − (材料費計 + 人件費計 + 委託費計 + 設備関係費計 + 経費等) = 医業利益
//   医業利益 + 医業外損益 = 経常利益
// がどちらも誤差 0 で成立する
// (2026-09-11 に dashboard.duckdb の 4 表 109 (月 × ソース) で実測検証済。
//  monthly 28 / rolling12 17 / budget 36 / budgetActual 28 — 線形性を疑うべき rolling12 も含む)。
//
// ★2026-09-11 ユーザ裁定: **医業外損益を 経費等 に畳まない**。
//   畳むと (a) 補助金が出た月に 経費等 がマイナスになって積み上げ棒が壊れ
//   (2026-03: 雑収入 390,524 千円 → 経費等 −270,555 千円)、
//   (b) 差異ランキングで補助金が「費用が減った＝良い」 に化ける
//   (2026-07: 経費等 予算差 −47,280 千円 のうち −38,819＝82% が医業外) ため。
//   独立させると 経費等 は全期間で正 (75,792〜128,204 千円) になり、 費用として素直に読める。
//
// ★旧実装の 経費等 は 研究費 / 研修費 を落としていたため、 ウォーターフォールの
//   「その他 (営業外等)」 に毎月 数千千円 の残差が出ていた。 さらに (予算比較).xls は
//   本部経費を「事務局経費」 という別名で持つため R 側の導出式 (本部経費 決め打ち) から
//   漏れ、 前期比較 由来の 経費等 とズレていた
//   (notes/20260525_pl_予算比較_引継ぎ.md「原因未調査」の正体)。 ここで別名を補完して解消する。
//   ★ズレ幅は年度で違う: FY2024 13,486 / FY2025 16,339 / FY2026 16,971 千円/月 (= その年度の本部経費)。
//
// ★別名の付き方は 2 通りあり、 一緒くたにしない (2026-09-11 実測):
//   - 経費側:   (前期比較)=本部経費 / (予算比較)=事務局経費 … **帳票の違い**で全年度そう。
//   - 人件費側: (前期比較)=本部人件費 / (予算比較)は **FY2024・FY2025=本部人件費、FY2026=事務局人件費**
//               … **年度で呼称が変わっている**。

import type { PlResponse } from "@shared/api";

/** 経費等 の構成要素 (医業費用のうち 材料費 / 人件費 / 委託費 / 設備関係費 以外) */
const KEIHI_COMPONENTS = ["経費計", "本部経費", "研究費", "研修費"] as const;
/** 医業外損益 = (雑収入 + 受取利息) − (雑損失 + 支払利息)。 **収益符号** (プラスが良い) */
const NONOP_PLUS  = ["雑収入", "受取利息"] as const;
const NONOP_MINUS = ["雑損失", "支払利息"] as const;

/** 派生項目の metric 名 */
export const KEIHI_METRIC = "経費等";
export const NONOP_METRIC = "医業外損益";

// 同じ項目が帳票 / 年度によって別名になっているもの。 経費等 の計算のためだけに補完する。
// ★補完は **この関数の中の Map に閉じる**。 返り値に別名の行を足すと、
//   差異ランキングの `wanted` に載っている項目 (事務局人件費 等) が
//   「予算側にその名前が無いので落ちていた年度」 に突然現れる = 宣言外の挙動変更になる
//   (2026-09-11 敵対レビュー指摘。 FY2024/FY2025 で実際に起きる)。
const NAME_ALIASES: readonly (readonly [string, string])[] = [
  ["事務局経費",   "本部経費"],
  ["事務局人件費", "本部人件費"],
] as const;

type Row = { date: string; metric: string; value: number };

/** 1 系列 (monthly / rolling12 / budgetMonthly / budgetActual) について
 *  - `経費等` の値を成分から作り直し (元データの値は使わない)
 *  - `医業外損益` の行を新たに足す (元データには無い項目)
 *  を行う。 */
function rebuildSeries<T extends Row>(rows: readonly T[]): T[] {
  if (rows.length === 0) return rows as T[];

  // date → metric → value
  // ★鍵に fiscal_year を持てない (server が PlBudgetPoint から落としている)。 実測では
  //   budget / budgetActual とも (month, metric) の重複は 0 件で、 R 側が fiscal_year ごとに
  //   1 ファイル・各ファイルは自年度 12 ヶ月だけを出すので構造的にも重複しない。
  //   それでも重複したら 後勝ちの成分で両方の行を書くことになるので、 黙らず警告する。
  const byDate = new Map<string, Map<string, number>>();
  for (const r of rows) {
    let m = byDate.get(r.date);
    if (!m) { m = new Map(); byDate.set(r.date, m); }
    if (m.has(r.metric) && m.get(r.metric) !== r.value) {
      console.warn(`[plBasis] 同じ (date, metric) に別の値: ${r.date} ${r.metric} ${m.get(r.metric)} → ${r.value}。年度の重なりを疑う`);
    }
    m.set(r.metric, r.value);
  }

  // 別名の補完 (足りない側だけ。 ★この Map の中だけに閉じ、 行としては返さない)
  for (const m of byDate.values()) {
    for (const [a, b] of NAME_ALIASES) {
      const va = m.get(a), vb = m.get(b);
      if (va != null && vb == null) m.set(b, va);
      else if (vb != null && va == null) m.set(a, vb);
    }
  }

  const keihiOf = (m: Map<string, number>) =>
    KEIHI_COMPONENTS.reduce((s, k) => s + (m.get(k) ?? 0), 0);
  const nonopOf = (m: Map<string, number>) =>
    NONOP_PLUS.reduce((s, k) => s + (m.get(k) ?? 0), 0)
    - NONOP_MINUS.reduce((s, k) => s + (m.get(k) ?? 0), 0);

  // ① 経費等 は in-place 置換 (元データに全月分ある)
  const out: T[] = rows.map((r) => {
    if (r.metric !== KEIHI_METRIC) return r;
    const m = byDate.get(r.date);
    return m ? { ...r, value: keihiOf(m) } : r;
  });

  // ② 医業外損益 は元データに無いので行を足す。
  //    ★「経費等 の行がある月」= PL として成立している月 にだけ足す (未来月に 0 を生やさない)。
  const monthsWithKeihi = new Set(rows.filter((r) => r.metric === KEIHI_METRIC).map((r) => r.date));
  const template = rows[0];
  for (const date of monthsWithKeihi) {
    const m = byDate.get(date);
    if (!m) continue;
    out.push({ ...template, date, metric: NONOP_METRIC, value: nonopOf(m) });
  }
  return out;
}

/** PlResponse の 4 系列すべてで 経費等 を作り直し、 医業外損益 を足した新しい response を返す。
 *  元の data は変更しない (react-query の cache をそのまま保つ)。 */
export function derivePlSeries(data: PlResponse): PlResponse {
  return {
    ...data,
    monthly:       rebuildSeries(data.monthly),
    rolling12:     rebuildSeries(data.rolling12),
    budgetMonthly: rebuildSeries(data.budgetMonthly),
    budgetActual:  rebuildSeries(data.budgetActual),
    // indices (基準月=100 の指数) は PL タブでは未使用なので触らない
  };
}

/** 経費等 の中身を明示した表示名。
 *
 *  ★本部経費 を名前から隠さない。 実測 28 ヶ月平均の構成比は 本部経費 15.7% / 研修費 2.5% で、
 *    名乗っている研修費より隠していた本部経費の方が 6 倍大きい (年 約 2 億円)。 本部経費
 *    (= 予算比較 xls の「事務局経費」) は帳票でも経費計の外に立つ独立行 = 本部からの配賦で、
 *    病院側で制御できない費目。「経費等が予算超過」 と出たとき、 配賦分か自前の経費かを
 *    名前で区別できないと読み違える。 */
export const KEIHI_LABEL = "経費等(経費+研修費+本部経費)";

/** 医業外損益が何かを一言で。 補助金 (雑収入) が入るので月によって大きく振れる。 */
export const NONOP_NOTE =
  "医業外損益 = 雑収入 + 受取利息 − 雑損失 − 支払利息 (プラスが収入超)。"
  + "補助金は雑収入に入るので月によって大きく振れる (26年03月は +373,206 千円)。"
  + "医業利益 + 医業外損益 = 経常利益。";
